import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { Search } from "lucide-react";

export default function SearchResults() {
  const [params, setParams] = useSearchParams();
  const { t, lang } = useLang();
  const [q, setQ] = useState(params.get("q") || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = async (query) => {
    if (!query || query.length < 2) return;
    setLoading(true);
    try {
      const { data } = await api.get("/ai/search", { params: { q: query, lang, limit: 20 } });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (params.get("q")) search(params.get("q"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const submit = (e) => {
    e.preventDefault();
    setParams({ q });
  };

  return (
    <div className="max-w-[1100px] mx-auto px-5 lg:px-10 py-16" data-testid="search-page">
      <div className="label-eyebrow text-ink mb-3">{t.common.ai_smart_search}</div>
      <h1 className="heading-serif text-4xl lg:text-5xl text-ink mb-8">{t.filters.search_placeholder.replace("…", "")}</h1>

      <form onSubmit={submit} className="flex border border-slate200 mb-10" data-testid="search-form">
        <span className="grid place-items-center px-3 text-slate500">
          <Search className="w-4 h-4" />
        </span>
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t.filters.search_placeholder}
          className="flex-1 bg-transparent h-12 text-[15px] outline-none"
          data-testid="search-input"
        />
        <button className="px-5 bg-ink text-white text-[12px] uppercase tracking-[0.2em]">{t.common.ai_smart_search}</button>
      </form>

      {loading ? (
        <div className="text-slate500 text-center py-16">{t.common.loading}</div>
      ) : items.length === 0 && params.get("q") ? (
        <div className="text-slate500 text-center py-16" data-testid="search-empty">{t.common.no_results}</div>
      ) : (
        <div className="grid gap-5">
          {items.map((it) => (
            <Link
              key={it.id}
              to={`/article/${it.slug}`}
              className="tile p-5 group block"
              data-testid={`search-result-${it.slug}`}
            >
              <div className="flex items-center gap-3 mb-2">
                <span className="label-eyebrow text-ink">{t.categories[it.category] || it.category}</span>
                {it.country && <span className="text-[10px] uppercase tracking-widest text-slate500">{it.country}</span>}
                {it.organization && <span className="text-[10px] uppercase tracking-widest text-slate500">{it.organization}</span>}
              </div>
              <h3 className="heading-serif text-xl text-ink mb-1 group-hover:opacity-80 transition-opacity">{it.title}</h3>
              <p className="text-[14px] text-slate600 line-clamp-2">{it.summary}</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
